/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent as F, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { IState } from "../../core/store/store";
import {
  addToProtocolAsync,
  swapBundleSavingIntoProtocolAsync,
  swapIndividualListingIntoProtocolAsync } from "../../core/store/protocol/protocol.thunks";
import MaUTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import LinkIcon from '@mui/icons-material/Link';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import AddIcon from '@mui/icons-material/Add';
import { PROTOCOL } from "../../core/router/paths";
import { deleteListingAsync } from "../../core/store/listings/listings.thunks";
import { useAppDispatch } from "../../core/store/hooks";
import { alphabeticalSort } from "../../utils/string";
import HeadingSort from "../UI/Table/HeadingSort";
import { handleToggleSortValues } from "../../utils/table";
import ListedPrice from "../Listings/ListedPrice";
import { IBundleSaving, TSavingRow } from "@protoplan/types";
import TextTooltip from "../UI/Tooltip/TextTooltip";
import { SortDirection, SortFn, TableHeading } from "../../core/types/component";


interface Props
{
  protocolId: number,
  savings: TSavingRow[],
  swapDosingId: number,
  selected?: number,
  handleToggleSelected: (savingId: number) => void
}

const SavingsTable: F<Props> = (props: Props) =>
{
  const savings = props.savings;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const user = useSelector((state: IState) => state.user);

  // Sorting (ascending = alphabetical, descending = reverse-alphabetical)
  const [listingSort, setListingSort] = useState<SortDirection | null>(null);
  const [vendorSort, setVendorSort] = useState<SortDirection | null>(null);
  const [priceSort, setPriceSort] = useState<SortDirection | null>(null);
  const [feesPerMonthSort, setFeesPerMonthSort] = useState<SortDirection | null>(null);
  const [costPerMonthSort, setCostPerMonthSort] = useState<SortDirection | null>(null);
  const [savingSort, setSavingSort] = useState<SortDirection | null>(SortDirection.descending);
  const allSetSorts = [
    setListingSort,
    setVendorSort,
    setPriceSort,
    setFeesPerMonthSort,
    setCostPerMonthSort,
    setSavingSort];

  const handleToggleSort = (
    key: string,
    sort: SortDirection | null,
    setSort: (value: React.SetStateAction<SortDirection | null>) => void,
    sortFn?: SortFn) =>
    handleToggleSortValues<TSavingRow>({
      values,
      setValues,
      key,
      sortDirection: sort,
      setSortDirection: setSort,
      unsetSortDirections: allSetSorts,
      sortFn });

  // Headings
  const determineHeadings = () =>
  {
    const baseHeadings: TableHeading[] = [
      { Header: '', accessor: 'addToProtocol' },
      { Header: '', accessor: 'swapIntoProtocol' },
      { Header: 'Name',
        accessor: 'listingName',
        sortDirection: listingSort,
        setSortDirection: setListingSort,
        sortFn: (a: string, b: string) => alphabeticalSort(a, b, true) },
      { Header: 'Vendor',
        accessor: 'vendorName',
        sortDirection: vendorSort,
        setSortDirection: setVendorSort,
        sortFn: (a: string, b: string) => alphabeticalSort(a, b, true) },
      {
        Header: 'Price listed',
        accessor: 'price',
        sortDirection: priceSort,
        setSortDirection: setPriceSort },
      {
        Header: 'Order fees per month',
        accessor: 'feesPerMonth',
        sortDirection: feesPerMonthSort,
        setSortDirection: setFeesPerMonthSort },
      {
        Header: 'Cost per month',
        accessor: 'costPerMonth',
        sortDirection: costPerMonthSort,
        setSortDirection: setCostPerMonthSort },
      {
        Header: 'Saving',
        accessor: 'saving',
        sortDirection: savingSort,
        setSortDirection: setSavingSort },
      { Header: '', accessor: 'link' } ];

    const userListings = !savings?.every(listing => !listing.userId);
    return baseHeadings.concat(userListings ? [{ Header: '', accessor: 'remove' }] : []);
  };

  const [headings, setHeadings] = useState(determineHeadings());

  // Values
  const determineValues = () => savings.map(s => ({ ...s, price: s.price + (s.deliveryPerListing || 0) }));
  const [values, setValues] = useState(determineValues().sort((a, b) => b.saving - a.saving));


  // Update headings and rows when listings arrive
  useEffect(() =>
  {
    setHeadings(determineHeadings());
    setValues(determineValues());
  }, []);

  // Update headings when sort state changed
  useEffect(() =>
  {
    setHeadings(determineHeadings());
  }, [listingSort, vendorSort, priceSort, feesPerMonthSort, costPerMonthSort, savingSort]);



  // Listing functionality
  const handleAddToProtocol = (listingId: number) =>
  {
    dispatch(addToProtocolAsync(props.protocolId, listingId));
    navigate(PROTOCOL);
  };

  const handleSwapIntoProtocol = (listing: TSavingRow) =>
  {
    if(listing.bundleId)
      appDispatch(swapBundleSavingIntoProtocolAsync(listing as unknown as IBundleSaving, props.protocolId));
    else
      appDispatch(swapIndividualListingIntoProtocolAsync(listing, props.swapDosingId, props.protocolId));

    navigate(PROTOCOL);
  };

  const handleRemove = async(rowIndex: number) =>
  {
    if(!savings)
      return;

    const listingId = savings[rowIndex].listingId;
    await appDispatch(deleteListingAsync(listingId));
    setValues(savings.filter(l => l.listingId !== listingId));
  };


  const padding =  { paddingLeft: 10, paddingRight: 10 };

  return (
    <MaUTable size='small'>
      <TableHead>
        <TableRow>
          {headings.map((column, columnIndex) =>
            <TableCell key={columnIndex} style={padding}>
              {(() =>
              {
                const title = column.Header;
                const accessor = column.accessor;

                if(column.sortDirection !== undefined && column.setSortDirection)
                {
                  return(
                    <HeadingSort
                      headingText={title}
                      sort={column.sortDirection}
                      handleToggleSort={handleToggleSort(
                        accessor,
                        column.sortDirection,
                        column.setSortDirection,
                        column.sortFn)} />);
                }
                else
                {
                  return <b>{title}</b>;
                }
              })()}
            </TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {values.map((row, rowIndex) =>
        {
          return (
            <TableRow
              key={rowIndex}
              onClick={() => props.handleToggleSelected(row.savingId)}
              style={{ backgroundColor: row.savingId === props.selected ? '#f9f9f9' : undefined }}>
              {headings
                .map(h => h.accessor)
                .map((key, index) =>
                {
                  switch(key)
                  {
                  case 'addToProtocol':
                    return(
                      <TableCell key={index} id='AddToProtocol' style={{ width: '10px', ...padding }}>
                        <TextTooltip lines={["Add to protocol"]}>
                          <AddIcon
                            onClick={() => handleAddToProtocol(row.listingId)}
                            style={{ cursor: 'pointer', color: 'green' }}/>
                        </TextTooltip>
                      </TableCell>);
                  case 'swapIntoProtocol':
                    return(
                      <TableCell key={index} style={{ width: '10px' }}>
                        {<TextTooltip lines={["Swap into protocol"]}>
                          <ShoppingCartCheckoutIcon
                            onClick={() => handleSwapIntoProtocol(row)}
                            style={{ cursor: 'pointer', color: 'gray' }}/>
                        </TextTooltip>}
                      </TableCell>);
                  case 'listingName':
                    return(
                      <TableCell key={index} style={padding}>
                        <Link
                          variant='body2'
                          underline='hover'
                          target='_blank'
                          rel='noreferrer'
                          style={{ cursor: 'pointer' }}
                          onClick={() => props.swapDosingId ?
                            handleSwapIntoProtocol(row) :
                            handleAddToProtocol(row.listingId)} >
                          {row[key]}
                        </Link>
                      </TableCell>);
                  case 'vendorName':
                    return(
                      <TableCell key={index} style={padding}>
                        {row[key]}
                      </TableCell>);
                  case 'price':
                    return(
                      <TableCell key={index} style={{ whiteSpace: 'nowrap', ...padding }}>
                        <ListedPrice listingId={row.listingId}/>
                      </TableCell>);
                  case 'saving':
                    return(
                      <TableCell key={index} style={{ whiteSpace: 'nowrap', ...padding }}>
                        {user.currencySymbol + " " + row[key].toFixed(2)}
                      </TableCell>);
                  case 'link':
                    return(
                      <TableCell key={index} style={{ width: '10px', ...padding }}>
                        <Box sx={{ height: 19 }}>
                          <TextTooltip lines={[row.url]}>
                            <Link
                              variant='body2'
                              underline='hover'
                              target='_blank'
                              rel='noreferrer'
                              style={{ cursor: 'pointer', color: 'gray' }}
                              href={row.url} >
                              <LinkIcon fontSize='small'/>
                            </Link>
                          </TextTooltip>
                        </Box>
                      </TableCell>);
                  case 'remove':
                    return(
                      (user.username && row.userId === user.userId) ?
                        <TableCell key={index} style={{ width: '10px', ...padding }}>
                          <Box id='Remove' sx={{ height: 19 }}>
                            <TextTooltip lines={["Delete listing"]}>
                              <RemoveCircleIcon
                                fontSize='small'
                                onClick={() => handleRemove(rowIndex)}
                                style={{ cursor: 'pointer', color: 'red' }}/>
                            </TextTooltip>
                          </Box>
                        </TableCell> :
                        <td key={index}/>);
                  }
                })}
            </TableRow>);
        })}
      </TableBody>
    </MaUTable>);
};

export default SavingsTable;
